import { forwardRef, useState } from 'react';
import {
  ConversationMessageDto,
  CreateLeadContactChatbotDto,
  CreateLeadContactChatbotDtoSchema,
  DisplayContactFormToolInvocation,
  ProblemDetail,
} from '@endion/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { VISITED_PROPERTY_URLS_STORAGE_KEY } from '../../hooks/real-estate/use-set-visited-property-url-to-session-storage.hook';
import { useChatContext } from '../../hooks/use-chat-context';
import { useCustomerChatbotContext } from '../../hooks/use-customer-chatbot-context';
import { leadsSendContact } from '../../queries/chatbot-action.query';
import { findServerSideSchemaValidationErrorPaths } from '../../utils/error-handler';
import withSubmitHandler from '../../utils/with-submit-handler';
import FormWrapper from '../form/form-wrapper';
import { InputFormSection } from '../form/input-form-section';
import { PhoneInput } from '../inputs/phone-input';
import SimpleInput from '../inputs/simple-input';
import { InformationContainer } from './information-container';

export const ContactForm = forwardRef(
  (
    { toolInvocation, message }: { toolInvocation: DisplayContactFormToolInvocation; message: ConversationMessageDto },
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { chatbotApiKey } = useCustomerChatbotContext();
    const { updateToolInvocationResult, submit, messages } = useChatContext();
    const [error, setError] = useState<string | undefined>(undefined);

    const {
      handleSubmit,
      register,
      control,
      setError: setFieldError,
      formState: { isSubmitting, errors, isSubmitSuccessful },
    } = useForm<CreateLeadContactChatbotDto>({
      defaultValues: {
        email: '',
        name: '',
        phone: '',
        visitedProperties: sessionStorage.getItem(VISITED_PROPERTY_URLS_STORAGE_KEY) || '',
      },
      resolver: zodResolver(CreateLeadContactChatbotDtoSchema),
    });

    if (toolInvocation.result?.name)
      return (
        <InformationContainer
          message={`Completaste el formulario de contacto exitosamente el ${new Date(message.createdAt).toLocaleDateString('es-AR')}`}
        />
      );

    if (messages.at(-1)?.id !== message.id)
      return <InformationContainer ref={ref} message="Este mensaje interactivo ha expirado." />;

    const handleSubmitError = (errorMessage: string, problemDetail?: ProblemDetail) => {
      if (!problemDetail) return setError(errorMessage);
      const erroredPaths = findServerSideSchemaValidationErrorPaths(problemDetail);
      // Phone numbers have an extra validation layer on the server.
      if (erroredPaths.includes('phone')) {
        return setFieldError('phone', {
          message: 'Número de teléfono inválido. Recordá ingresar el código de país y el código de area correctamente.',
        });
      }
      return setError(errorMessage);
    };

    const onSubmit = withSubmitHandler(async (formData: CreateLeadContactChatbotDto) => {
      await leadsSendContact({
        chatbotApiKey,
        payload: formData,
      });
      const updatedMessages = updateToolInvocationResult('displayContactForm', message.id, toolInvocation.toolCallId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      await submit(updatedMessages);
    }, handleSubmitError);

    return (
      <div ref={ref}>
        <FormWrapper handleSubmit={handleSubmit} onSubmit={onSubmit}>
          <InputFormSection isDisabled={isSubmitting} error={error} isSubmitSuccessful={isSubmitSuccessful}>
            <div className="text-md font-semibold">
              Completá el siguiente formulario y nos comunicaremos a la brevedad.
            </div>
            <SimpleInput
              {...register('name')}
              label="Nombre completo"
              placeholder="Ingresá tu nombre"
              type="text"
              error={errors.name}
            />
            <SimpleInput
              {...register('email')}
              label="Email"
              placeholder="Ingresá tu email"
              type="email"
              error={errors.email}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  {...field}
                  label="Número de teléfono"
                  defaultCountry="ar"
                  placeholder="Ingresá tu teléfono"
                  error={errors.phone}
                  helperText={'Por ejemplo: +54 11 2345 6789'}
                />
              )}
            />
          </InputFormSection>
        </FormWrapper>
      </div>
    );
  },
);

ContactForm.displayName = 'ContactForm';
